import { useSeoTags } from "../useSeoTags";
import "../marketMyths.css"; // reused for .sm-table-wrap/.sm-table -- same table styling as the evidence docs
import "../workingPaper.css";

type SectionStatus = "drafted" | "draft" | "pending";

interface Section {
  num: number;
  title: string;
  desc: string;
  status: SectionStatus;
}

const SECTIONS: Section[] = [
  { num: 1, title: "The replication gap in equity-anomaly testing", desc: "Why most reported market claims don't survive honest out-of-sample testing, illustrated first-hand from this project's own forensic audit.", status: "drafted" },
  { num: 2, title: "Pre-registration protocol", desc: "Fixing hypotheses and pass/fail criteria before seeing results; the blessing gate feature claims already pass through.", status: "drafted" },
  { num: 3, title: "Apparatus & data", desc: "The platform as reproducible test-bench: universe, horizons, rolling-origin out-of-sample design.", status: "drafted" },
  { num: 4, title: "Placebo calibration & false-discovery control", desc: "White-noise and random-walk placebos; Benjamini-Hochberg FDR across each tested family.", status: "drafted" },
  { num: 5, title: "Results — two findings, in full", desc: "An index-direction null and a post-earnings-drift positive. The empirical core so far.", status: "drafted" },
  { num: 6, title: "Anomaly decay & signal half-life", desc: "How fast a surviving edge decays. Needs the half-life module, which isn't built yet.", status: "pending" },
  { num: 7, title: "Discussion — nulls as contribution", desc: "Why an honest null is worth publishing; what this paper explicitly does not claim.", status: "drafted" },
  { num: 8, title: "Limitations", desc: "A measured instrument bias, coverage gaps, horizon scope, and single-market scope — stated up front.", status: "drafted" },
];

const STATUS_META: Record<SectionStatus, { label: string; cls: string }> = {
  drafted: { label: "drafted", cls: "wp-ostat-done" },
  draft: { label: "in draft", cls: "wp-ostat-draft" },
  pending: { label: "pending data", cls: "wp-ostat-pend" },
};

function Outline() {
  return (
    <div className="wp-outline">
      <h2>Structure</h2>
      {SECTIONS.map((s) => {
        const meta = STATUS_META[s.status];
        return (
          <div className="wp-oitem" key={s.num}>
            <span className="wp-onum">{s.num}</span>
            <div className="wp-obody">
              <div className="wp-ost">{s.title}</div>
              <div className="wp-osd">{s.desc}</div>
            </div>
            <span className={`wp-ostat ${meta.cls}`}>{meta.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function ResultsTable() {
  return (
    <div className="wp-rp">
      <h2>Results — in full</h2>
      <div className="wp-rp-d">§5. Both findings below are reported in full, including the parts that failed, with a direct link to the complete evidence.</div>
      <div className="sm-table-wrap">
        <table className="sm-table">
          <thead>
            <tr><th>Claim tested</th><th>OOS verdict</th><th>Note</th><th></th></tr>
          </thead>
          <tbody>
            <tr>
              <td className="wp-anom">Index direction is forecastable (5–30d, 16 NSE indices)</td>
              <td><span className="wp-rv wp-rv-null">null</span></td>
              <td>no validated directional edge in 4 independent tests; our own earlier model audited and found worse than a naive baseline on 11/16 indices</td>
              <td><a href="/market-myths?evidence=index-direction">evidence →</a></td>
            </tr>
            <tr>
              <td className="wp-anom">Post-earnings-announcement drift (SUE-PEAD)</td>
              <td><span className="wp-rv wp-rv-dep">it depends</span></td>
              <td>real 60-day drift in 2 of 10 sectors, concentrated in thinner-coverage names; placebo-clean</td>
              <td><a href="/market-myths?evidence=sue-pead">evidence →</a></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WorkingPaperPage() {
  // Signed off public 2026-10-26, after two review rounds (initial content,
  // then a pre-registration-precision fix -- only SUE-PEAD was formally
  // pre-registered, the index-direction leg had methodology fixed before
  // results but was not, and every blanket "pre-registered" claim implying
  // otherwise was corrected throughout). The noindex-on-mount guard used
  // during review is removed; this page is now indexable like the rest of
  // the public site, launched explicitly as a v0.1 living draft.

  useSeoTags({
    title: "Honest by Construction — Working Paper — Tickerpicker",
    description: "A living research paper on placebo-calibrated, false-discovery-controlled testing of " +
      "equity-market claims on Indian (NSE) equities. The contribution is the discipline and its honest " +
      "nulls, not a new anomaly.",
    canonical: "https://tickerpicker.in/working-paper",
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ScholarlyArticle",
      headline: "Honest by Construction: Placebo-Calibrated, False-Discovery-Controlled Testing of Equity-Market Claims on Indian Equities",
      description: "A living research paper on placebo-calibrated, false-discovery-controlled testing of equity-market claims on Indian (NSE) equities.",
      url: "https://tickerpicker.in/working-paper",
      author: { "@type": "Person", name: "Praveen" },
      publisher: { "@type": "Organization", name: "Tickerpicker" },
    },
  });

  return (
    <div className="wp-shell">
      <nav className="wp-nav">
        <a href="/" className="wp-brand"><span className="dot" />TickerPicker</a>
        <div className="wp-navlinks">
          <a href="/">Home</a>
          <a href="/market-myths">Market Myths</a>
        </div>
      </nav>

      <div className="wp-page">
        <div className="wp-main">
          <div className="wp-ptype">Working Paper · living draft</div>
          <h1 className="wp-ptitle">Honest by Construction: Placebo-Calibrated, False-Discovery-Controlled Testing of Equity-Market Claims on Indian Equities</h1>
          <div className="wp-pauthors"><b>Praveen</b> · Walsoul Consulting Pvt Ltd</div>
          <div className="wp-pmeta">v0.1 (living) · last updated 26 Oct 2026 · keywords: pre-registration, false-discovery control, Indian equities, NSE, reproducibility</div>

          <div className="wp-summary">
            <h3>Summary, in plain language</h3>
            <p>
              This is a living research paper about <b>how</b> we test ideas about Indian stock markets — not about
              any one trading idea. The central claim is methodological: most claims about market behavior don't
              hold up once tested properly — checking a result wasn't just luck, comparing it against random noise,
              and correcting for testing many things at once. This paper applies that discipline to two real
              questions on NSE data, in full, including where it says no: can you predict which way an index will
              move (no — and we checked our own earlier model and found it didn't work either), and does a stock
              keep drifting after an earnings surprise (yes, but only in specific, narrow conditions we state
              precisely). More tests join this paper as they're completed and written up the same way. Nothing
              here is investment advice.
            </p>
          </div>

          <div className="wp-abstract">
            <h3>Abstract</h3>
            <p>
              We apply a placebo-calibrated, false-discovery-controlled testing protocol — formally pre-registered
              for one of the two results reported here, with methodology fixed before results for the other (see
              §2) — to claims about Indian (NSE) equity-market behavior. The contribution is not a new anomaly but{" "}
              <b>the discipline itself and its honest results</b>: we report, in full, both a null result —
              daily/weekly index direction is not forecastable across 16 NSE indices in four independent tests, a
              finding corroborated by a forensic audit of our own earlier deployed indices model's overstated
              accuracy claim — and a formally pre-registered, narrow positive result — post-earnings-announcement
              drift exists in 2 of 10 testable sectors, concentrated in thinner-coverage names, surviving a matched
              placebo and false-discovery correction in every tested bucket. We document the apparatus that makes
              each claim reproducible — pre-registration where formally applied, placebo calibration, and
              false-discovery correction throughout — and we state plainly which further tests remain pending or
              unproven, rather than padding the record.
            </p>
          </div>

          <Outline />

          <ResultsTable />

          <div className="wp-section">
            <h2>1. The replication gap in equity-anomaly testing</h2>
            <p>
              Most claims about what predicts stock or index returns are never tested the way they need to be to
              mean anything: out-of-sample, against a calibrated null, with a correction for how many things were
              actually tried. A result that looks significant at the 95% level is <i>expected</i> to appear by
              chance roughly one time in twenty — and when dozens or hundreds of candidate signals, indices,
              regimes, or frequencies are searched, that arithmetic alone produces a steady supply of
              plausible-looking false positives. The discipline that catches this — non-overlapping sampling,
              permutation-derived significance thresholds, embargoed walk-forward validation, comparison against
              the true empirical base rate, and a multiple-testing correction sized to the actual number of tests
              run — is not exotic; it is simply rarely applied in full, together, to the same claim.
            </p>
            <p>
              This project has first-hand, forensic evidence of the gap. We built and deployed an earlier indices
              model ourselves, which reported a headline 62.7% average directional accuracy across 16 NSE indices.
              Applying the full validation stack to that same system retroactively — using its own saved
              artifacts, not a re-implementation — found its reported accuracy was <i>worse than simply guessing
              the market's own majority outcome</i> for 11 of its 16 indices. The system's own codebase contained a
              correct walk-forward validation function; it was simply never the one invoked to produce the
              reported numbers. That gap — between the correct method existing somewhere in a codebase and the
              correct method actually producing the headline number — is the gap this paper's apparatus is built
              to close by construction, not by discipline alone. The full audit is reported in §5 and the linked
              evidence.
            </p>
          </div>

          <div className="wp-section">
            <h2>2. Pre-registration protocol</h2>
            <p>
              Every result in this paper follows the same rule: the hypothesis, the test design, and the pass/fail
              criteria are fixed <i>before</i> the result is seen, not adjusted afterward to fit it. In practice
              this means the horizon, the segmentation (sector, depth tier, or similar), the significance test, and
              the multiple-comparison correction are all committed in writing ahead of running the analysis — and
              when a design decision changes mid-investigation (for example, widening a cohort-minimum threshold),
              that change and its reasoning are recorded, not silently absorbed into a cleaner-looking final number.
            </p>
            <p>
              <b>This paper is precise about which of the two §5 results cleared the stricter, formal bar.</b> The
              SUE-PEAD investigation was <b>formally pre-registered</b>: methodology, bucket grid, and pass/fail
              criteria were fixed in writing, in advance, confirmed before execution. The index-direction
              investigations had their methodology and validation framework fixed before any result was examined —
              the discipline above — but were <b>not formally pre-registered</b> in that stricter, written-in-advance
              sense; the evidence document itself describes them as "pre-registered-in-spirit." Both are honest
              research, held to the same out-of-sample and placebo standard; only one carries the formal label, and
              this paper does not blur that distinction to make the methodology sound more uniform than it was.
            </p>
            <p>
              This mirrors the platform's own internal <b>blessing gate</b>: a feature earns a place in the live
              models only after clearing a pre-registered out-of-sample test with a permutation-derived
              significance threshold and a placebo control run through the identical pipeline — the same apparatus
              this paper's own findings are held to, not a separate, lighter-weight standard invented for
              publication.
            </p>
          </div>

          <div className="wp-section">
            <h2>3. Apparatus &amp; data</h2>
            <p>
              The test-bench is this project's own production platform: a point-in-time NSE/BSE equity database
              (daily bars from 2016, survivorship-handled identity merges), a cross-sectional stock-segment
              universe (sector, liquidity, and capitalization tiers), and 16–18 NSE indices. Every test in this
              paper is run against that same live database via a committed, version-controlled script — never a
              one-off notebook whose exact state can't be reconstructed.
            </p>
            <p style={{ color: "var(--warn)" }}>
              <b>Correction (2026-10-27):</b> this page previously described the database as
              "corporate-action-adjusted." That is the intended design, but a data-hygiene investigation found
              the CA confirm step has never run on production — splits/bonuses are currently unadjusted for
              roughly 21%+ of the universe. Remediation is pending; this methodology description is corrected to
              not claim adjustment that isn't currently in effect.
            </p>
            <p style={{ color: "var(--warn)" }}>
              <b>Update (2026-10-31):</b> the correction above is now substantially resolved. Prices and derived
              features are adjusted for every <b>confirmed</b> split/bonus on a frozen, point-in-time basis, and
              the platform's own scorecard/analytics surfaces now read from that corrected layer. A specific,
              bounded set of ambiguous historical actions — ratio conflicts, single-source-only matches, and
              complex multi-leg cases — remains under individual admin review and is not yet adjusted for. This
              is <b>"adjusted for confirmed actions, with a known set still pending review,"</b> not full
              coverage; this note will be updated again once that review closes.
            </p>
            <p>
              Out-of-sample evaluation uses <b>rolling-origin, embargoed walk-forward validation</b>: training and
              test windows never share information, and an explicit embargo gap (at least the forecast horizon)
              sits at the boundary, specifically to prevent a forward-looking target's own construction from
              leaking across it. Significance testing is computed on <b>non-overlapping, independent-block
              samples</b> — overlapping forward-return windows share information and are not independent evidence,
              so treating them as if they were would overstate confidence in exactly the way this paper's apparatus
              exists to prevent.
            </p>
          </div>

          <div className="wp-section">
            <h2>4. Placebo calibration &amp; false-discovery control</h2>
            <p>
              Two placebo controls recur across this project's tests: a <b>white-noise placebo</b> (a random score
              with no relationship to the outcome, run through the identical pipeline as the real candidate, in
              every tested bucket) confirms the test's own false-positive rate is calibrated rather than inflated;
              and, for structural/temporal claims specifically, a <b>shuffled-surrogate ("random-walk") null</b>
              benchmarks a result against what a series with no real structure would produce by chance, rather than
              against a textbook distributional assumption this project has no basis to make about Indian equity
              returns.
            </p>
            <p>
              Where multiple buckets, features, or frequencies are tested together, a <b>Benjamini-Hochberg
              false-discovery-rate correction</b> is applied across the full, pre-specified family — sized to the
              actual number of tests run, not to a convenient subset chosen after seeing which ones looked
              promising. A bucket that cannot be tested at all (too little history, too few independent blocks) is
              reported as <i>insufficient_history</i>, not silently dropped from the family or folded into a
              passing result.
            </p>
          </div>

          <div className="wp-section">
            <h2>5. Results — the two findings, in full</h2>
            <p>
              Both results below are reported in full — synthesized here at a level a general reader can follow,
              with every number, every test, and every failure case in the linked evidence document. This paper
              deliberately does not restate that evidence: one source of truth per finding, so the public record
              can never quietly drift from the technical one.
            </p>
            <p>
              <b>Index direction (null).</b> Four independent tests — a blessed technical/macro feature sweep, an
              expanded search including market breadth and foreign-investor flows, calendar and cyclical structure,
              and cross-sectional rotation among the indices themselves — found no validated directional edge
              across 16 NSE indices at horizons from 5 to 30 trading days. A forensic audit of our own earlier
              indices model corroborates the null from an entirely independent angle: its claimed 62.7% accuracy
              underperforms a naive majority-class baseline on 11 of 16 indices once properly validated. The same
              method applied to individual stocks instead of whole indices <i>does</i> find real, validated
              signal — ruling out "the method is just too weak to find anything" as an explanation. Full evidence:
              see the linked Research Log document.
            </p>
            <p>
              <b>Post-earnings-announcement drift (narrow positive).</b> Standardized Unexpected Earnings, tested
              as a dedicated event-panel (not forced through a daily-feature pipeline SUE is structurally
              unsuited to), shows a real, placebo-clean, 60-trading-day drift effect — but only in 2 of 10 testable
              sectors (Consumer Cyclical, Industrials), concentrated in names with thinner earnings-reporting
              history, and only on the 60-day horizon, never at the short horizon a trader would act on
              immediately. A third sector's result that looked significant at first glance failed the
              pre-registered sign-consistency check — reported as the demonstration that the discipline works, not
              suppressed. This is a research finding, not a tradeable signal: it has not been checked against
              round-trip transaction costs, and the underlying earnings data skews toward surviving, larger-cap
              names. Full evidence: see the linked Research Log document.
            </p>
          </div>

          <div className="wp-section">
            <h2>6. Anomaly decay &amp; signal half-life</h2>
            <p className="wp-pending-note">
              <b>In preparation.</b> This section needs the platform's signal half-life measurement (entry-delay
              and holding-period decay curves), which is a planned V2 module not yet built. No content is
              published here until that evidence exists — this status is stated plainly rather than filled with
              placeholder prose.
            </p>
          </div>

          <div className="wp-section">
            <h2>7. Discussion — nulls as contribution</h2>
            <p>
              The two results above are, on their face, asymmetric: one is a clean null, the other a narrow
              positive. Read together, they are the same methodological point from two directions. The
              index-direction null was reached only after the identical apparatus had already been shown, via a
              genuine positive control at the individual-stock level, to be capable of finding and confirming a
              real effect when one exists — so the null is not an artifact of an overly conservative test. The
              SUE-PEAD result survived precisely because the same apparatus refused a result (Financial Services'
              short-horizon leg) that a less disciplined test would have reported as a third positive finding. An
              honest null, methodology fixed before the result and placebo-calibrated, is not an absence of a
              result; it is evidence, reported in the same voice and to the same standard as a positive one.
            </p>
            <p>
              What this paper does not claim: that index direction is unforecastable by any possible means
              (intraday data, options-implied signals, and alternative data are untested here); that
              post-earnings drift is tradeable once costs are considered; or that the two results generalize
              beyond the specific horizons, universes, and periods tested. Each claim's scope is stated precisely
              in its own evidence document, not rounded up here for effect.
            </p>
          </div>

          <div className="wp-section">
            <h2>Related work</h2>
            <p className="wp-pending-note">
              <b>In preparation, pending verified sources.</b> This paper's apparatus follows established practice
              in the data-snooping and multiple-testing literature, and its two results sit alongside documented
              strands on market efficiency, momentum, and post-earnings drift. Citing that work correctly requires
              verifying real, specific sources rather than asserting one from memory — the same discipline that
              keeps an LLM out of any number path elsewhere in this project. An internal
              literature-search guide (topics and search terms, no invented citations) exists for this purpose and
              will be resolved into real references before this section is populated.
            </p>
          </div>

          <div className="wp-section">
            <h2>8. Limitations</h2>
            <p>
              <b>A measured, not assumed, instrument bias.</b> A synthetic control test of this platform's own
              incremental-feature-testing instrument found a small, roughly flat false-positive floor (on the
              order of +0.003 in residual rank-IC) at moderate correlation with an existing feature — a real,
              characterized limitation of the testing instrument itself, not of any individual result, disclosed
              rather than corrected for retroactively.
            </p>
            <p>
              <b>Coverage gaps.</b> The SUE-PEAD result runs on a 1,037-security subset (45% of the model-eligible
              universe) with confirmed, non-provisional earnings history visible to its data source — skewed
              toward surviving, larger-cap names. A name that delisted, or was never covered by that source, cannot
              appear in this result, and nothing in the design corrects for that.
            </p>
            <p>
              <b>Horizon scope.</b> Both results are scoped to specific, stated horizons (5–30 trading days for
              index direction; the sector's own champion-model horizon and a 60-day post-earnings window for
              SUE-PEAD). Neither claim extends to horizons outside what was actually tested.
            </p>
            <p>
              <b>Single market.</b> Every result in this paper is specific to NSE/BSE-listed Indian equities over
              the periods tested. No claim is made that either result transfers to other markets or eras.
            </p>
          </div>

          <div className="wp-section">
            <h2>Reproducibility</h2>
            <p>
              Every result traces to a version-controlled script run against this project's own database, with
              full per-test provenance. The index-direction paper's four scripts and the SUE-PEAD event-panel
              script are named in their own evidence documents (linked above), along with the exact design each
              followed — formally pre-registered for SUE-PEAD, methodology fixed before results for index-direction
              (§2). This is a closed-source, private research project; the underlying database and
              full historical bar/flow data are not currently public. "Reproducible" here means the committed
              scripts can be re-run against the project's own database to obtain the same results — not that an
              external reader can independently verify the underlying data without such access.
            </p>
          </div>
        </div>

        <div className="wp-rail">
          <div className="wp-rcard">
            <h4>Version</h4>
            <div className="wp-vrow"><span>Living draft</span><span className="wp-vbadge wp-vbadge-live">v0.1 · current</span></div>
            <div className="wp-vrow"><span>Frozen PDF</span><span className="wp-vbadge wp-vbadge-frozen">not yet</span></div>
            <div className="wp-vrow"><span>Freeze trigger</span><b>evidence mature</b></div>
          </div>
          <div className="wp-rcard">
            <h4>Staged release</h4>
            <p>The paper lives on the web and updates as research lands (<b>v0.x</b>). When the evidence base is mature, a <b>frozen, citable PDF</b> (v1.0) is cut for submission — the web version keeps evolving past it. No PDF exists yet.</p>
          </div>
          <div className="wp-rcard">
            <h4>Reproducibility</h4>
            <p>Every result links to its <b>full evidence</b> in the Research Log, via Market Myths: Tested. The platform is the apparatus; the claims are re-runnable, not asserted.</p>
          </div>
        </div>

        <div className="wp-footnote">
          Personal, non-commercial research project. Nothing in this paper is investment advice. Every verdict and
          number traces to a linked evidence document — this page synthesizes, it never restates the full record.
        </div>
      </div>
    </div>
  );
}
